import { interfaceTranslator } from "@/i18n/translate";

/**
 * What a reader is told when we have nothing honest and specific to say. The
 * house fallback: every surface that shows a failure reaches for this rather
 * than inventing its own wording or forwarding the engine's.
 */
export function genericMessage(): string {
  return interfaceTranslator("common.errors")("generic");
}

/** What a reader is told when a lapsed subscription refuses a write. */
export function subscriptionRequiredMessage(): string {
  return interfaceTranslator("common.errors")("subscriptionRequired");
}

/**
 * What a reader is told when the request never reached the server. Lives here
 * beside {@link NETWORK_UNREACHABLE} so the status and the sentence that
 * explains it cannot drift apart.
 */
export function networkUnreachableMessage(): string {
  return interfaceTranslator("common.errors")("networkUnreachable");
}

/**
 * `status` for a request that never reached the server, so no HTTP code exists.
 * Lives beside the error rather than in the fetch layer because readers need it
 * to tell "offline" from "the server answered and refused" — the distinction
 * that decides whether blaming the connection is honest.
 */
export const NETWORK_UNREACHABLE = 0;

/**
 * Error thrown by the API fetch layer (`apiRequest`, `transcribeAudioBlob`) on a
 * non-2xx response. `message` is ALWAYS safe to show to a user; the raw server
 * body — which can be a technical string like `{"error":"Internal Server
 * Error"}` — is kept on `detail` for logging and must never reach the UI.
 */
export class ApiRequestError extends Error {
  readonly status: number;
  /**
   * Raw response body. For logging; never render it as text. A field read out
   * of it through a shared schema (`connectRefusalOf`) is no longer raw.
   */
  readonly detail: string;

  constructor({
    status,
    detail,
    userMessage,
    cause,
  }: {
    status: number;
    detail: string;
    userMessage?: string;
    /** The underlying failure, when one exists — e.g. the `TypeError` a
     *  never-completed `fetch` rejects with. Kept for logging, never rendered. */
    cause?: unknown;
  }) {
    super(userMessage?.trim() || genericMessage(), { cause });
    this.name = "ApiRequestError";
    this.status = status;
    this.detail = detail;
  }
}
